"use client"; // Mark as a Client Component since it handles events

import { Typography } from "@aph/ui-lib-humanx";
import React, { useEffect, useRef, useState } from "react";
import { ArticleDetailInterface } from "@/components/ArticlePage/api/interface";
import ArticleComponentMapping from "@/components/ArticlePage/articleComponentMapping";
import InThisArticle from "@/components/ArticlePage/Components/InThisArticle";
import ArticleSelector from "@/components/ArticlePage/redux/selectors/articleData";
import { useSyncArticleData } from "@/components/ArticlePage/syncArticleData";
import Breadcrumbs, { BreadcrumbOptionsInterface } from "@/components/Breadcrumbs";
import routes from "@/helpers/router";
import { DeviceInfo } from "@/interface/deviceInfo";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import NavBar from "@/components/Header/NavBar";
import { ProductInfo } from "@/components/ShoppingComponent/types/productInfo";
import { COMMON_COMPONENT_TYPES, NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import IconLeft from "$/icons/icon-left.svg";
import { DoctorDetails } from "@/redux/slice/doctors/types/FetchDoctorsPayload";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import { pageViewEvent } from "@/helpers/TSDEvents";
import SegmentInfoSelector from "@/redux/slice/auth/selectors/userSegmentInfo";
import { setReferer } from "@/redux/slice/global/slice";
import { DiagnosticDetailInterface } from "@/redux/slice/diagnostics/types/fetchDiagnosticsPayload";
import styles from "./article.module.scss";

interface ArticlePageProps {
  articleData: any;
  ua: DeviceInfo;
  referer?: string;
  articleSlug: string;
  userOnBoarded: boolean;
  productInfo?: {
    [key: number | string]: ProductInfo[];
  };
  currentSpeciality: string;
  doctorsArray: DoctorDetails[];
  diagnosticsArray: DiagnosticDetailInterface[];
  isPharma?: boolean;
}

const ArticlePage: React.FC<ArticlePageProps> = (props) => {
  const dispatch = useAppDispatch();
  const {
    articleData,
    ua,
    referer,
    articleSlug,
    userOnBoarded,
    productInfo,
    currentSpeciality,
    doctorsArray,
    diagnosticsArray,
    isPharma = false,
  } = props;

  useSyncArticleData({ ArticleData: articleData, ua, articleSlug });
  const articleInfoObject: ArticleDetailInterface = useAppSelector(ArticleSelector);
  const SegmentInfoObject = useAppSelector(SegmentInfoSelector);
  const [isOpenLink, setIsOpenLink] = useState<boolean>(false);
  const [isShowArticleFlyBottom, setIsShowArticleFlyBottom] = useState<boolean>(false);
  const [articleSectionTitle, setArticleSectionTitle] = useState<string>("");
  const signedInStatus = useAppSelector(signedInStateSelector);

  // Create a ref object to store section references
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  const articleInfoFromRedux = articleInfoObject?.data?.[0]?.attributes;
  const articleInfoFromProps = articleData?.data?.[0]?.attributes;
  const articleDataObject = articleInfoFromRedux || articleInfoFromProps;

  const categorySlug = articleDataObject?.ArticleCategory?.data?.attributes?.Slug;
  const pillarSlug = articleDataObject?.Pillar?.data?.attributes?.Slug;
  const clusterSlug = articleDataObject?.Cluster?.data?.attributes?.Slug;

  const generateBreadcrumbs = () => {
    const categoryName = articleDataObject?.ArticleCategory?.data?.attributes?.CategoryName;
    let breadCrumbs: BreadcrumbOptionsInterface[] = [];

    if (isPharma) {
      // For pharma pages, add home and blog links
      breadCrumbs.push({
        label: "Home",
        url: "https://www.apollopharmacy.in/",
      });
      breadCrumbs.push({
        label: "Blog",
        url: "https://www.apollopharmacy.in/blog",
      });

      if (articleDataObject?.Title && articleDataObject?.Slug) {
        breadCrumbs.push({
          label: articleDataObject.Title,
          url: `https://www.apollopharmacy.in/blogs/article/${articleDataObject.Slug}`,
        });
      }
    } else {
      // For momverse pages, use existing logic
      if (categoryName && categorySlug) {
        breadCrumbs.push({
          label: categoryName,
          url: routes.category(categorySlug),
        });
      }

      const pillarName = articleDataObject?.Pillar?.data?.attributes?.Name;

      if (pillarName && pillarSlug) {
        breadCrumbs.push({
          label: pillarName,
          url: routes.pillar(pillarSlug),
        });
      }

      const clusterName = articleDataObject?.Cluster?.data?.attributes?.Name;

      if (clusterName && clusterSlug) {
        breadCrumbs.push({
          label: clusterName,
          url: routes.cluster(clusterSlug),
        });
      }

      if (articleDataObject?.Title && articleDataObject?.Slug) {
        breadCrumbs.push({
          label: articleDataObject.Title,
          url: routes.article(articleDataObject.Slug),
        });
      }
    }

    return breadCrumbs;
  };

  const breadCrumbsArray: BreadcrumbOptionsInterface[] = generateBreadcrumbs();

  const toggleDropdown = () => {
    setIsOpenLink(!isOpenLink);
  };

  const hideListDropdown = () => {
    setIsOpenLink(false);
  };

  const navBarInfo = articleInfoObject?.data?.[0]?.attributes?.ArticleContent?.find(
    (section: any) => section.ComponentType === COMMON_COMPONENT_TYPES.NavBar
  );

  const articleSections = articleInfoObject?.data?.[0]?.attributes?.ArticleContent.filter(
    (section: any) => section?.ComponentType !== COMMON_COMPONENT_TYPES.NavBar
  );
  const leftPanelSections = articleSections?.filter((section: any) => section?.Position === "Left");
  const rightPanelSections = articleSections?.filter(
    (section: any) => section?.Position !== "Left" && section?.Position !== "Bottom"
  );
  const fullWidthSections = articleSections?.filter((section: any) => section?.Position === "Bottom");
  const fullWidthComponentTypes = fullWidthSections?.map((section: any) => section.ComponentType);

  useEffect(() => {
    dispatch(setReferer(referer));

    if (signedInStatus !== SignedinState.UNRESOLVED) {
      const isUserLoggedin = signedInStatus === SignedinState.SIGNEDIN;

      pageViewEvent({
        page: "article",
        page_name: "Article Page",
        is_loggedin: isUserLoggedin ? true : false,
        momverse_stage: userOnBoarded ? (ONBOARDING_STATUS[SegmentInfoObject?.type] ?? "") : NON_ONBOARDED_STATUS,
        type: "article",
        nav_src: referer || "direct",
      });
    }
  }, [signedInStatus]);

  return (
    <>
      {ua?.isDesktop && !isPharma && (
        <NavBar
          navBarData={navBarInfo}
          userOnBoarded={userOnBoarded}
          userAgent={ua}
          pathname={routes.article(articleSlug)}
        />
      )}
      {breadCrumbsArray?.length > 0 && (
        <div className={`breadcrumb container`}>
          <Breadcrumbs options={breadCrumbsArray} />
        </div>
      )}
      {ua?.isMobile && (
        <div className={`${styles.articleinsideSection}`}>
          <>
            {articleSections?.map((section: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  <div className={`${fullWidthComponentTypes?.includes(section.ComponentType) ? `` : `container`}`}>
                    <ArticleComponentMapping
                      data={section}
                      options={{
                        hideListDropdown,
                        sectionRefs,
                        setIsShowArticleFlyBottom,
                        setArticleSectionTitle,
                        setSectionRef,
                        categorySlug,
                        pillarSlug,
                        clusterSlug,
                        productInfo,
                        currentSpeciality,
                        doctorsArray,
                        index,
                        diagnostics: diagnosticsArray,
                        isPharma,
                      }}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </>

          {isShowArticleFlyBottom && (
            <div className={styles.stickyLinksRoot}>
              <div className={styles.rootLink} onClick={toggleDropdown}>
                <label className={styles.labelText}>in this article</label>
                <Typography component="p" className={styles.title}>
                  {articleSectionTitle}
                </Typography>
              </div>
              <IconLeft height={24} width={24} className={styles.ico} />
              {isOpenLink && (
                <div className={`${styles.whenLickInBottom}`}>
                  <InThisArticle
                    {...{
                      hideListDropdown,
                      sectionRefs,
                      setIsShowArticleFlyBottom,
                      setArticleSectionTitle,
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {ua?.isDesktop && (
        <>
          <div className={`${styles.columnsContainer} container`}>
            <div className={styles.leftSection}>
              {leftPanelSections.map((section: any, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <ArticleComponentMapping
                      data={section}
                      options={{
                        hideListDropdown,
                        sectionRefs,
                        setIsShowArticleFlyBottom,
                        setArticleSectionTitle,
                        setSectionRef,
                        ua,
                        categorySlug,
                        pillarSlug,
                        clusterSlug,
                        isPharma,
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </div>
            <div className={styles.rightSection}>
              {rightPanelSections?.map((section: any, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <ArticleComponentMapping
                      data={section}
                      options={{
                        hideListDropdown,
                        sectionRefs,
                        setIsShowArticleFlyBottom,
                        setArticleSectionTitle,
                        setSectionRef,
                        categorySlug,
                        pillarSlug,
                        clusterSlug,
                        productInfo,
                        currentSpeciality,
                        doctorsArray,
                        index,
                        diagnostics: diagnosticsArray,
                        isPharma,
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div>
            {fullWidthSections?.map((section: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  <ArticleComponentMapping
                    data={section}
                    options={{
                      hideListDropdown,
                      sectionRefs,
                      setIsShowArticleFlyBottom,
                      setArticleSectionTitle,
                      setSectionRef,
                      categorySlug,
                      pillarSlug,
                      clusterSlug,
                      currentSpeciality,
                      doctorsArray,
                      index,
                      diagnostics: diagnosticsArray,
                      isPharma,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default ArticlePage;
